
import result from './api/result'
import axios from 'axios'

const API_ORIGIN = process.env.VUE_APP_API_ORIGIN;
const API_CONFIG = {
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
}
  
const OK = 200;


/**
 * API呼び出し.
 * API実行前後でエラー情報を収集する.
 * @param {Object} context 
 * @param {Object} apiOptions
 * @param {Function} callback 
 * @returns callbackの実行結果
 */
const apiCall = async (context, apiOptions, callback) => {
  try {
    if( !apiOptions.noDisplayLoading ){
      context.dispatch("view/start", null, { root: true });
    }
    context.commit('setApiError', null)

    return await callback()
  } catch(e) {
    context.commit('setApiError', { error: e })
    console.log('system error caught:', e)
  } finally {
    if( !apiOptions.noDisplayLoading ){
      context.dispatch("view/end", null, { root: true });
    }
  }
}

const SYSTEM_ERROR_DESCRIPTION = '予期せぬエラーが発生しました。サポートにお問い合わせください。'
const DEFAULT_ERROR_DESCRIPTION = 'エラーが発生しました。サポートにお問い合わせください。'

/**
 * レスポンスにエラーを含むかどうかを返す.
 * エラーがある場合は、apiError ステートにエラー情報をセットする.
 * @param {function} commit stateコミット
 * @param {object} response APIレスポンス
 * @param {object} descriptions メッセージ（キー：エラーコード、値：メッセージ）
 * @param {object} apiOptions
 * @param {array} okCodes OKとするコード
 * @returns エラーがある場合はtrue, それ以外はfalse
 */
const checkResponseError = (commit, response, descriptions, apiOptions = {}, okCodes = [OK]) => {
  if (response.status !== OK) {
    commit('setApiError', { httpStatus: response.status, SYSTEM_ERROR_DESCRIPTION, ...apiOptions })
    console.log('API system error')
    console.log(response.status)
    return true;
  }
  
  if (!okCodes.includes(response.data.code)) {
    const description = descriptions[response.data.code] || DEFAULT_ERROR_DESCRIPTION;

    commit('setApiError', { apiResponseCode: response.data.code, description, ...apiOptions })
    console.log('API error')
    console.log(response.data)
    return true;
  }
  return false;
}

const set = property => (state, payload) => (state[property] = payload)
const get = property => (state) => (state[property])

export default {
  namespaced: true,
  modules: {
    result,
  },
  state: {
    apiError: null,
  },
  getters: {
    apiError: get('apiError'),
  },
  mutations: {
    setApiError: set('apiError'),
  },
  actions: {
    /** 蔵衛門ID作成API */
    async createKuraemonId(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/createkuraemonid";
        const postParam = {
          mail: args.mail,
          password: args.password,
          company_name: args.company_name,
          user_name: args.user_name,
          industry: args.industry,
          receive_notification: args.receive_notification,
          no_operation: args.no_operation,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '登録に必要なパラメータが不足しています。',
          401: '蔵衛門クラウドユーザー登録済みのメールアドレスです。',
          402: 'パスワード強度が不足しています。',
          403: 'メールアドレスの形式が不正です。',
        }
        checkResponseError(context.commit, response, descriptions, apiOptions, [OK, 201])

        return response.data.code
      })
    },
    /** 蔵衛門ID本登録API */
    async registerKuraemonId(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/registerkuraemonid";
        const postParam = {
          onetime_token: args.onetime_token,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '登録に必要なパラメータが不足しています。',
          401: '蔵衛門クラウドユーザー仮登録の有効期限が過ぎています。',
          402: '蔵衛門クラウドユーザー仮登録の有効期限が過ぎています。',
        }
        checkResponseError(context.commit, response, descriptions, apiOptions)
      })
    },
    /** 蔵衛門IDログインAPI */
    async loginKuraemonId(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/loginkuraemonid";
        const postParam = {
          mail: args.mail,
          password: args.password,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        checkResponseError(context.commit, response, {}, apiOptions)
      })
    },
    /** 蔵衛門IDパスワード再設定用メール送信API */
    async sendKuraemonIdPasswordResetMail(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/sendkuraemonidpasswordresetmail";
        const postParam = {
          mail: args.mail,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        checkResponseError(context.commit, response, {}, apiOptions)
      })
    },
    /** トークン有効期限確認API */
    async checkTokenActive(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/checktokenactive";
        const postParam = {
          onetime_token: args.onetime_token,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          401: '有効期限が切れています。',
        }

        const ngToken = checkResponseError(context.commit, response, descriptions, apiOptions)

        return (ngToken ? '' : 'OK')
      })
    },
    /** 蔵衛門IDパスワード再設定API */
    async resetKuraemonIdPassword(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/resetkuraemonidpassword";
        const postParam = {
          onetime_token: args.onetime_token,
          password: args.password,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        checkResponseError(context.commit, response, descriptions, apiOptions)
      })
    },
    /** クラウド工事一覧取得API */
    async getCloudConstructionList(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/getcloudconstructionlist";
        const postParam = {
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }
        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.cloud_construction_list;
        context.commit('result/setCloudConstructionList', apiResult);
        return apiResult;
      })
    },
    /** クラウド工事作成API */
    async createCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/createcloudconstruction";
        const postParam = {
          construction_name: args.construction_name,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        checkResponseError(context.commit, response, descriptions, apiOptions)
      })
    },
    /** クラウド工事削除API */
    async deleteCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/deletecloudconstruction";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        checkResponseError(context.commit, response, descriptions, apiOptions)
      })
    },
    /** クラウド工事s3接続情報取得API */
    async getCloudConstructionS3Info(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/getcloudconstructions3info";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }
        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.s3_info;
        context.commit('result/setS3Info', apiResult);
        return apiResult;
      })
    },
    /** 黒板リスト取得API */
    async getKokubanDataList(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/getkokubandatalist";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
          is_deleted_only: args.is_deleted_only,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }
        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.kokubandata_list;
        context.commit('result/setKokubandataList', apiResult);
        return apiResult;
      })
    },
    /** 黒板データ作成・送信API */
    async makeKokubanData(context, args) {
      const MAKE_KOKUBAN_DATA_VERSION = 1
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/makekokubandata";
        const postParam = {
          serial_id: args.serial_id,
          version: MAKE_KOKUBAN_DATA_VERSION,
          cloud_construction_id: args.cloud_construction_id,
          created_key_list: args.created_key_list,
        }

        const TIMEOUT_OK_ERROR_MESSSAGE = 'kokuban-client-ok-timeout-error';
        try {
          context.commit('result/setSentKokubanWithTimeout', false);
          const makeKokubanDataApiConfig = {
            ...API_CONFIG,
            timeout: 10000,
            timeoutErrorMessage: TIMEOUT_OK_ERROR_MESSSAGE,
          }
          const response = await axios.post(url, postParam, makeKokubanDataApiConfig)

          const descriptions = {
            400: '必要なパラメータが不足しています。',
          }

          checkResponseError(context.commit, response, descriptions, apiOptions)
        } catch(e) {
          if(e.message === TIMEOUT_OK_ERROR_MESSSAGE){
            // OK TIMEOUT（※API Gatewayのタイムアウト30秒対策）
            context.commit('result/setSentKokubanWithTimeout', true);
            return;
          }
          throw e;
        }
      })
    },
    /** クラウド工事データ更新API */
    async updateCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/updatecloudconstruction";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
          update_items: args.update_items,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }
        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.update_cloud_construction_info;
        context.commit('result/setUpdateCloudConstructionInfo', apiResult);

        // 更新結果を「cloudConstructionInfo」に設定する
        const cloudConstructionInfo = context.getters['result/cloudConstructionInfo'];
        cloudConstructionInfo.is_auto_kokuban_transmission = apiResult.is_auto_kokuban_transmission;
        context.commit('result/setCloudConstructionInfo', cloudConstructionInfo);
        return apiResult;
      })
    },
    /** 黒板データ削除API */
    async deleteKokubanDataList(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/deletekokubandatalist";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
          created_key_list: args.created_key_list,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.error_list;
      })
    },
    /** 黒板データ削除解除API */
    async unDeleteKokubanDataList(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/undeletekokubandatalist";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
          created_key_list: args.created_key_list,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.error_list;
      })
    },
    /** クラウド工事共有API */
    async startShareCloudConstruction(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/startsharecloudconstruction";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
          mail_addr_list: args.mail_addr_list,
          message: args.message,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        checkResponseError(context.commit, response, descriptions, apiOptions)
      })
    },
    /** 写真データ取得API */
    async getPhotoData(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/getphotodata";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
          shooting_ymd: args.shooting_ymd,
          search_string: args.search_string,
          photo_key: args.photo_key,
          recent: args.recent,
          is_deleted_only: args.is_deleted_only,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        return {
          photoList: response.data.photo_list,
          resultCount: response.data.result_count,
          recent: response.data.recent,
        }
      })
    },
    /** クラウド工事写真状態取得API */
    async getCloudConstructionPhotoState(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/getcloudconstructionphotostate";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.state;
      })
    },
    /** 蔵衛門クラウドWeb画面バージョン取得API */
    async getKuraemonCloudWebPageVersion(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/getkuraemoncloudwebpageversion";
        const postParam = {
          web_page_code: args.web_page_code,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.version;
      })
    },
    /** クラウド工事情報取得API */
    async getCloudConstructionInfo(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/getcloudconstructioninfo";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }
        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.construction_info; // API戻りはconstruction_infoだが、
        context.commit('result/setCloudConstructionInfo', apiResult); // Vuexには cloud_construction_info としておく
        return apiResult;
      })
    },
    /** 黒板検索候補文字リスト取得API */
    async getKokubanSuggestWordList(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/getkokubansuggestwordlist";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }
        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = response.data.word_list;
        context.commit('result/setWordList', apiResult);
        return apiResult;
      })
    },
    /** 写真削除API */
    async deletePhotoList(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/deletephotolist";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
          photo_key_list: args.photo_key_list,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.error_list;
      })
    },
    /** 写真削除解除API */
    async unDeletePhotoList(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/undeletephotolist";
        const postParam = {
          cloud_construction_id: args.cloud_construction_id,
          photo_key_list: args.photo_key_list,
        }

        const response = await axios.post(url, postParam, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        if (checkResponseError(context.commit, response, descriptions, apiOptions)) {
          return;
        }
        return response.data.error_list;
      })
    },
    /** ログアウトAPI */
    async logoutKuraemonId(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/logoutkuraemonid";

        const response = await axios.post(url, {}, API_CONFIG)

        const descriptions = {
          400: '必要なパラメータが不足しています。',
        }

        checkResponseError(context.commit, response, descriptions, apiOptions)
      })
    },
    /** ログインチェックAPI */
    async checkLoginKuraemonId(context, args) {
      const apiOptions = args.__apiOptions || {}

      return await apiCall(context, apiOptions, async () => {
        const url = API_ORIGIN + "/checkloginkuraemonid";

        const response = await axios.post(url, {}, API_CONFIG)

        const noSession = checkResponseError(context.commit, response, {}, apiOptions)
        if (noSession) {
          return ''; // NG
        }
        context.commit('result/setUserInfo', response.data.user_info);
        return 'OK';
      })
    },
  },
}
